'use strict';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDeviceType } from './util';
/**
 * Realtime conversational session: WebSocket transport + mic capture + audio
 * playback, ported from the widget's `RealtimeAssistantManager`.
 *
 * Differences from the original (per the search-plugin migration spec):
 *  - Connects to a FIXED backend endpoint with NO auth (no `Authorization`
 *    query param, no token in the init payload).
 *  - AudioWorklet asset URLs are resolved from an injected `assetBase` instead
 *    of the global `window.__ZOE_WIDGET_BASE_URL__`.
 *
 * The session is a passive renderer driver: speech recognition, turn-taking
 * and TTS all happen server-side; the client streams mic PCM up and plays the
 * audio that streams back, exposing a playback gain node for lip-sync.
 */
class RealtimeSession {
    constructor(avatarId, projectId, sessionId, endpoint, assetBase) {
        this.communicationType = 'text';
        this.ws = null;
        this.messageHandlers = new Map();
        // Mic capture state (AudioWorklet-based)
        this.audioContext = null;
        this.audioWorkletNode = null;
        this.mediaStream = null;
        this.isStreaming = false;
        this.onAudioLevelCallback = null;
        // Response tracking for barge-in / interruption
        this.currentResponseId = null;
        this.currentResponseItemId = null;
        this.cancelledResponseIds = new Set();
        // The proxy in front of the avatar service drops a socket after ~30 s without traffic, which ends
        // the conversation whenever the customer reads the results in silence. Reconnecting would start a
        // new session and lose the conversation, so keep the existing one warm instead. The backend ignores
        // unknown message types by design.
        this.keepAliveTimer = null;
        this.avatarId = avatarId;
        this.projectId = projectId;
        this.sessionId = sessionId;
        this.endpoint = endpoint;
        this.assetBase = assetBase;
        this.audioPlayer = new RealtimeAudioPlayer(assetBase);
    }
    /** Open the WebSocket. Resolves once open. */
    connect() {
        return new Promise((resolve, reject) => {
            const cleanApiUrl = this.endpoint.replace(/\/$/, '');
            const wsBaseUrl = cleanApiUrl.replace(/^http/, 'ws');
            const deviceType = getDeviceType();
            // No API key in this integration — the session is keyless.
            // `sessionId` is passed as a connection query param: the backend reads
            // it from there (not the init payload) and uses it as the search-result
            // persistence key, echoing it back in `session.created`.
            const wsUrl = `${wsBaseUrl}/projects/${encodeURIComponent(this.projectId)}/avatars/session/${encodeURIComponent(this.avatarId)}?X-Device-Type=${deviceType}&sessionId=${encodeURIComponent(this.sessionId)}`;
            console.log('[avatar] Connecting to WebSocket:', wsUrl);
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => {
                // No handshake frame: avatarId and sessionId are already in the connection URL, and the
                // backend has no `init` case — it silently discards unknown types.
                this.startKeepAlive();
                resolve();
            };
            this.ws.onmessage = (event) => {
                let message;
                try {
                    message = JSON.parse(event.data);
                }
                catch (e) {
                    // One malformed frame must not skip the response-lifecycle bookkeeping below for
                    // every frame after it.
                    console.warn('[avatar] Ignoring unparseable frame');
                    return;
                }
                // Track response lifecycle for interruption handling
                if (message.type === 'response.created') {
                    if (message.response) {
                        this.currentResponseId = message.response.id;
                    }
                }
                else if (message.type === 'response.done' || message.type === 'response.cancelled') {
                    const responseId = message.response_id || (message.response && message.response.id);
                    if (responseId && responseId === this.currentResponseId) {
                        this.currentResponseId = null;
                        this.cancelledResponseIds.delete(responseId);
                    }
                }
                // Track item_id for truncation on barge-in
                if (message.type === 'response.output_item.added' || message.type === 'response.audio.delta') {
                    if (message.item_id) {
                        this.currentResponseItemId = message.item_id;
                    }
                }
                // Server-side VAD speech events
                if (message.type === 'input_audio_buffer.speech_started') {
                    this.handleSpeechStarted(message);
                }
                if (message.type === 'input_audio_buffer.speech_stopped') {
                    this.handleSpeechStopped(message);
                }
                // Audio playback — only in voice mode (avatar normalizes to voice)
                if (message.type === 'response.audio.delta' && this.communicationType === 'voice') {
                    const responseId = message.response_id;
                    if (responseId && this.cancelledResponseIds.has(responseId)) {
                        return; // discard stale audio from a cancelled response
                    }
                    this.audioPlayer.addChunk(message.delta, responseId);
                }
                // Fan out to registered handlers (subtitles, tools, mood, …)
                this.emit(message.type, message);
            };
            this.ws.onerror = (error) => {
                console.error('[avatar] WebSocket error:', error);
                this.emit('error', { message: 'WebSocket connection failed' });
                reject(error);
            };
            this.ws.onclose = () => {
                this.stopKeepAlive();
                this.emit('close', undefined);
            };
        });
    }
    startKeepAlive() {
        this.stopKeepAlive();
        this.keepAliveTimer = window.setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.sendMessage({ type: 'keep_alive' });
            }
        }, RealtimeSession.KEEP_ALIVE_INTERVAL_MS);
    }
    stopKeepAlive() {
        if (this.keepAliveTimer !== null) {
            window.clearInterval(this.keepAliveTimer);
            this.keepAliveTimer = null;
        }
    }
    /** Send a JSON message if the socket is open. */
    sendMessage(payload) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(payload));
        }
        else {
            console.warn('[avatar] Cannot send message: WebSocket not open', payload.type);
        }
    }
    /** Subscribe to a message type (or `'error'` / `'close'`). */
    on(eventType, handler) {
        if (!this.messageHandlers.has(eventType)) {
            this.messageHandlers.set(eventType, []);
        }
        this.messageHandlers.get(eventType).push(handler);
    }
    emit(eventType, data) {
        const handlers = this.messageHandlers.get(eventType) || [];
        handlers.forEach((h) => h(data));
    }
    resumeAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.audioPlayer) {
                return this.audioPlayer.resume();
            }
            return undefined;
        });
    }
    /** Playback AudioContext (24 kHz) — used by the renderer for lip-sync. */
    getAudioContext() {
        return this.audioPlayer ? this.audioPlayer.audioContext : null;
    }
    /** Playback gain node all audio flows through — tapped for lip-sync. */
    getPlaybackGainNode() {
        return this.audioPlayer ? this.audioPlayer.playbackGainNode : null;
    }
    /**
     * Progress through the current turn's speech, for pacing the written transcript against the voice.
     * See {@link RealtimeAudioPlayer#speechProgress}.
     */
    getSpeechProgress() {
        return this.audioPlayer
            ? this.audioPlayer.speechProgress()
            : { playedMs: 0, receivedMs: 0 };
    }
    /**
     * Normalize and set the communication type. `'avatar'` is a rendering-only
     * concept; for audio/OpenAI it is identical to `'voice'`.
     */
    setCommunicationType(type) {
        const normalized = type.toLowerCase();
        this.communicationType = normalized === 'avatar' ? 'voice' : normalized;
    }
    /**
     * Push a `session.update` to the backend. Only forwards valid OpenAI
     * Realtime fields (the backend relays this raw to OpenAI). Ported as-is.
     */
    updateSession(sessionConfig) {
        const { communicationType } = sessionConfig;
        if (communicationType) {
            this.communicationType = communicationType.toLowerCase();
        }
        const validOpenAiFields = [
            'modalities', 'instructions', 'voice', 'input_audio_format',
            'output_audio_format', 'input_audio_transcription', 'turn_detection',
            'tools', 'tool_choice', 'temperature', 'max_response_output_tokens'
        ];
        const legacyVoiceMap = { fable: 'sage', onyx: 'ash', nova: 'shimmer' };
        const cleanSession = {};
        validOpenAiFields.forEach((field) => {
            if (sessionConfig[field] !== undefined) {
                let value = sessionConfig[field];
                if (field === 'voice' && typeof value === 'string') {
                    value = value.toLowerCase();
                    if (legacyVoiceMap[value]) {
                        console.warn(`[avatar] Legacy voice '${value}' remapped to '${legacyVoiceMap[value]}'`);
                        value = legacyVoiceMap[value];
                    }
                }
                cleanSession[field] = value;
            }
        });
        if (!cleanSession.modalities) {
            cleanSession.modalities = this.communicationType === 'voice' ? ['text', 'audio'] : ['text'];
        }
        this.sendMessage({ type: 'session.update', session: cleanSession });
    }
    /**
     * Start mic capture via AudioWorklet, streaming PCM16 to the backend.
     * @param onAudioLevel optional RMS-level callback (0..1) for the mic icon
     * @param prewarmStream optional MediaStream acquired in the user gesture (iOS)
     */
    startRecording(onAudioLevel_1) {
        return __awaiter(this, arguments, void 0, function* (onAudioLevel, prewarmStream = null) {
            try {
                this.onAudioLevelCallback = onAudioLevel || null;
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
                if (!this.audioContext.audioWorklet) {
                    throw new Error('AudioWorklet is not supported in this browser/context (requires HTTPS).');
                }
                if (this.audioContext.state === 'suspended') {
                    yield this.audioContext.resume();
                }
                yield this.audioContext.audioWorklet.addModule(`${this.assetBase}/audio-processor.js`);
                if (prewarmStream) {
                    this.mediaStream = prewarmStream;
                }
                else {
                    this.mediaStream = yield navigator.mediaDevices.getUserMedia({
                        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }
                    });
                }
                const source = this.audioContext.createMediaStreamSource(this.mediaStream);
                this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor');
                this.audioWorkletNode.port.onmessage = (event) => {
                    const msg = event.data;
                    if (msg.type === 'audio') {
                        this.sendMessage({ type: 'input_audio_buffer.append', audio: this.int16ArrayToBase64(msg.data) });
                    }
                    else if (msg.type === 'audio-level') {
                        if (this.onAudioLevelCallback) {
                            this.onAudioLevelCallback(msg.level);
                        }
                    }
                };
                // mic → worklet (no destination — avoids echoing mic to the speakers)
                source.connect(this.audioWorkletNode);
                this.isStreaming = true;
                console.log('[avatar] AudioWorklet recording started');
            }
            catch (error) {
                console.error('[avatar] Failed to start recording:', error);
                this.cleanupRecording();
                throw error;
            }
        });
    }
    /** Stop mic capture; commits the buffer in text (push-to-talk) mode. */
    stopRecording() {
        if (!this.isStreaming && !this.audioWorkletNode) {
            return;
        }
        this.isStreaming = false;
        if (this.communicationType === 'text') {
            this.sendMessage({ type: 'input_audio_buffer.commit' });
        }
        this.cleanupRecording();
    }
    cleanupRecording() {
        if (this.audioWorkletNode) {
            try {
                this.audioWorkletNode.disconnect();
            }
            catch (e) { /* ignore */ }
            this.audioWorkletNode = null;
        }
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => track.stop());
            this.mediaStream = null;
        }
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close().catch(() => { });
            this.audioContext = null;
        }
        this.onAudioLevelCallback = null;
        this.isStreaming = false;
    }
    int16ArrayToBase64(int16Array) {
        const bytes = new Uint8Array(int16Array.buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }
    /** Barge-in: stop playback and tell the backend how much was heard. */
    handleSpeechStarted(event) {
        if (this.audioPlayer.hasActiveSources()) {
            const audioEndMs = this.audioPlayer.stopAll();
            if (this.currentResponseItemId) {
                this.sendMessage({
                    type: 'conversation.item.truncate',
                    item_id: this.currentResponseItemId,
                    content_index: 0,
                    audio_end_ms: Math.round(audioEndMs)
                });
            }
            if (this.currentResponseId) {
                this.cancelledResponseIds.add(this.currentResponseId);
            }
        }
        this.emit('speech_started', event);
    }
    handleSpeechStopped(event) {
        this.emit('speech_stopped', event);
    }
    /** Close the socket and release all audio resources. */
    disconnect() {
        this.stopKeepAlive();
        if (this.ws) {
            try {
                this.ws.close();
            }
            catch (e) { /* ignore */ }
            this.ws = null;
        }
        this.cleanupRecording();
        if (this.audioPlayer) {
            this.audioPlayer.stop();
        }
        this.currentResponseId = null;
        this.currentResponseItemId = null;
        this.cancelledResponseIds.clear();
    }
}
RealtimeSession.KEEP_ALIVE_INTERVAL_MS = 15000;
export default RealtimeSession;
/**
 * AudioWorklet-based PCM16 playback queue (24 kHz), ported from the widget.
 * Exposes a `playbackGainNode` that all audio flows through so the renderer can
 * tap it for lip-sync analysis.
 */
class RealtimeAudioPlayer {
    constructor(assetBase) {
        this.audioContext = null;
        this.playbackGainNode = null;
        this.playbackWorkletNode = null;
        this.initPromise = null;
        this.pendingChunkBuffers = [];
        this.workletReady = false;
        this.bufferedSamples = 0;
        this.playedSamples = 0;
        this.isPlaying = false;
        this.underrunCount = 0;
        this.lastStateAt = 0;
        this.assetBase = assetBase;
    }
    init() {
        if (this.initPromise) {
            return this.initPromise;
        }
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
            // The playback worklet writes one queued PCM sample per output frame and does not resample,
            // so the stream only sounds right while the context actually runs at 24 kHz. Browsers may
            // refuse the requested rate and fall back to the device default (48 kHz is typical), and
            // then every voice plays an octave high. Loud, because it is otherwise invisible from here
            // and presents as 'the voice sounds wrong' rather than as an error.
            if (this.audioContext.sampleRate !== 24000) {
                console.error('[avatar] Playback AudioContext is running at '
                    + `${this.audioContext.sampleRate} Hz, not the 24000 Hz the audio is encoded at. `
                    + 'Pitch and speed will be wrong; the playback worklet does not resample.');
            }
            this.playbackGainNode = this.audioContext.createGain();
            this.playbackGainNode.connect(this.audioContext.destination);
        }
        if (!this.audioContext.audioWorklet) {
            throw new Error('AudioWorklet playback is not supported in this browser/context.');
        }
        this.initPromise = this.audioContext.audioWorklet.addModule(`${this.assetBase}/audio-playback-processor.js`)
            .then(() => {
            if (!this.playbackWorkletNode) {
                this.playbackWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-playback-processor', {
                    numberOfInputs: 0,
                    numberOfOutputs: 1,
                    outputChannelCount: [1]
                });
                this.playbackWorkletNode.port.onmessage = (event) => {
                    const msg = event.data;
                    if (msg.type !== 'playback-state') {
                        return;
                    }
                    this.bufferedSamples = msg.bufferedSamples || 0;
                    this.playedSamples = msg.playedSamples || 0;
                    this.lastStateAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                    this.isPlaying = !!msg.started;
                    if ((msg.underrunCount || 0) > this.underrunCount) {
                        console.warn('[avatar] Playback underrun detected', {
                            bufferedMs: Math.round(this.bufferedSamples / 24),
                            playedMs: Math.round(this.playedSamples / 24),
                            underrunCount: msg.underrunCount
                        });
                    }
                    this.underrunCount = msg.underrunCount || 0;
                };
                this.playbackWorkletNode.connect(this.playbackGainNode);
            }
            this.workletReady = true;
            this.flushPendingChunks();
        })
            .catch((error) => {
            this.initPromise = null;
            console.error('[avatar] Failed to initialize playback worklet:', error);
            throw error;
        });
        return this.initPromise;
    }
    resume() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.init();
            if (this.audioContext.state === 'suspended') {
                try {
                    yield this.audioContext.resume();
                    return true;
                }
                catch (e) {
                    console.error('[avatar] Failed to resume AudioContext:', e);
                    return false;
                }
            }
            return true;
        });
    }
    flushPendingChunks() {
        if (!this.workletReady || !this.playbackWorkletNode) {
            return;
        }
        while (this.pendingChunkBuffers.length > 0) {
            const pcmBuffer = this.pendingChunkBuffers.shift();
            this.playbackWorkletNode.port.postMessage({ type: 'enqueue', pcmBuffer }, [pcmBuffer]);
        }
    }
    /**
     * How far through this turn's speech we are: milliseconds already out of the speaker, and
     * milliseconds received so far. Their ratio is what paces the avatar's written transcript against
     * its own voice, and it needs no words-per-second constant: both numbers grow while the turn
     * streams, and when the model stops sending, `playedMs` catches up to `receivedMs`, so the last
     * character lands with the last sound.
     *
     * The worklet posts its state on its own schedule, so `playedMs` is extrapolated with the wall
     * clock between those messages - otherwise the text would advance in visible steps at whatever
     * cadence the messages happen to arrive. Never reports more played than received.
     */
    speechProgress() {
        let pendingSamples = 0;
        this.pendingChunkBuffers.forEach((b) => { pendingSamples += b.byteLength / 2; });
        const receivedMs = (this.playedSamples + this.bufferedSamples + pendingSamples) / 24;
        let playedMs = this.playedSamples / 24;
        if (this.isPlaying && this.lastStateAt) {
            const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            playedMs += Math.max(0, now - this.lastStateAt);
        }
        return { playedMs: Math.min(playedMs, receivedMs), receivedMs };
    }
    hasActiveSources() {
        return this.isPlaying || this.bufferedSamples > 0 || this.pendingChunkBuffers.length > 0;
    }
    /** Flush everything immediately (barge-in). Returns ms of audio played. */
    stopAll() {
        const playedMs = this.playedSamples / 24;
        this.pendingChunkBuffers = [];
        this.bufferedSamples = 0;
        this.playedSamples = 0;
        this.lastStateAt = 0;
        this.isPlaying = false;
        if (this.playbackWorkletNode) {
            try {
                this.playbackWorkletNode.port.postMessage({ type: 'flush' });
            }
            catch (e) { /* ignore */ }
        }
        return playedMs;
    }
    addChunk(base64Audio, responseId) {
        try {
            this.init();
        }
        catch (error) {
            console.error('[avatar] Failed to initialize audio playback:', error);
            return;
        }
        let bytes;
        try {
            const binary = atob(base64Audio);
            bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
        }
        catch (e) {
            console.error('[avatar] Failed to decode audio chunk:', e);
            return;
        }
        if (bytes.byteLength === 0) {
            return;
        }
        const pcmBuffer = bytes.buffer;
        if (!this.workletReady || !this.playbackWorkletNode) {
            this.pendingChunkBuffers.push(pcmBuffer);
            this.init().catch(() => { this.pendingChunkBuffers = []; });
            return;
        }
        this.playbackWorkletNode.port.postMessage({ type: 'enqueue', pcmBuffer, responseId }, [pcmBuffer]);
    }
    stop() {
        this.pendingChunkBuffers = [];
        this.bufferedSamples = 0;
        this.playedSamples = 0;
        this.lastStateAt = 0;
        this.isPlaying = false;
        this.underrunCount = 0;
        if (this.playbackWorkletNode) {
            try {
                this.playbackWorkletNode.port.postMessage({ type: 'flush' });
                this.playbackWorkletNode.disconnect();
            }
            catch (e) { /* ignore */ }
            this.playbackWorkletNode = null;
        }
        if (this.audioContext) {
            this.audioContext.close().catch(() => { });
            this.audioContext = null;
        }
        this.playbackGainNode = null;
        this.workletReady = false;
        this.initPromise = null;
    }
}
